import { useState } from 'react';
import { Trash2, FolderOpen } from 'lucide-react';

/**
 * Shown after map completion if there are removed (CUT) concepts.
 * User sees their completed map first, THEN this asks: save or delete?
 * "Start a new map" lives on the Dashboard when they view Random Thoughts.
 */
export default function RemovedConceptsOverlay({ cutTiles, onDelete, onSaveForLater, onSkip }) {
  const [saving, setSaving] = useState(false);

  if (!cutTiles || cutTiles.length === 0) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8">
        <h2 className="text-lg font-heading font-bold uppercase tracking-wide text-black mb-2">
          You removed {cutTiles.length} {cutTiles.length === 1 ? 'concept' : 'concepts'}
        </h2>
        <p className="text-sm text-txt-secondary mb-4">
          These did not make it into your Clarity Map. Want to save them for a future map?
        </p>

        {/* Show the removed concepts */}
        <div className="bg-canvas rounded-xl p-3 mb-5 max-h-40 overflow-auto">
          <div className="flex flex-wrap gap-2">
            {cutTiles.map((t) => (
              <span key={t.id} className="bg-black/10 text-txt-primary rounded-lg px-3 py-1.5 text-sm font-medium">
                {t.text}
              </span>
            ))}
          </div>
        </div>

        {/* Two options */}
        <div className="space-y-2.5">
          <button
            onClick={async () => {
              setSaving(true);
              await onSaveForLater();
            }}
            disabled={saving}
            className="w-full flex items-center gap-3 px-5 py-3.5 rounded-xl border-2 border-black/15 hover:border-teal hover:bg-teal/5 transition-all text-left disabled:opacity-50"
          >
            <FolderOpen className="w-5 h-5 text-teal-dark shrink-0" />
            <div>
              <p className="text-sm font-heading font-bold uppercase text-black">Save for later</p>
              <p className="text-xs text-txt-secondary">Keep them in Random Thoughts on your Dashboard</p>
            </div>
          </button>

          <button
            onClick={onDelete}
            className="w-full flex items-center gap-3 px-5 py-3.5 rounded-xl border-2 border-black/15 hover:border-brand-red hover:bg-red-50 transition-all text-left"
          >
            <Trash2 className="w-5 h-5 text-brand-red shrink-0" />
            <div>
              <p className="text-sm font-heading font-bold uppercase text-black">Delete permanently</p>
              <p className="text-xs text-txt-secondary">Remove these concepts for good</p>
            </div>
          </button>
        </div>

        <button
          onClick={onSkip}
          className="w-full text-center text-xs text-txt-secondary hover:text-txt-primary mt-4 transition-colors"
        >
          Decide later
        </button>
      </div>
    </div>
  );
}
